import styled from "@emotion/styled";
import {
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { TextField } from "@shared/components/TextField";
import { isUuid } from "@shared/utils/validators";
import { usersInitialState, UsersQueryParams } from "@store/users.store";
import { useCallback, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { blurActiveElement } from "@shared/utils/blur-active-element";

const FiltersContainer = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  justify-content: flex-end;
  align-items: flex-end;
  gap: 1rem;
  margin-bottom: 0.5rem;

  ${(p) => p.theme.media.mobileDown} {
    grid-template-columns: 1fr;
  }
`;

type Props = {
  usersQueryParams: UsersQueryParams;
  updateUsersQueryParams: (params: UsersQueryParams) => void;
};

type ExclusiveFilterKeys = keyof Omit<UsersQueryParams, "limit" | "page">;

const exclusiveFilters: Array<{ key: ExclusiveFilterKeys; name: string }> = [
  {
    key: "nameFilter",
    name: "Name",
  },
  {
    key: "emailFilter",
    name: "Email",
  },
  {
    key: "trackingIdFilter",
    name: "Tracking ID",
  },
];

type ErrorState = {
  [K in ExclusiveFilterKeys]?: { message: string };
};

type FilterState = Omit<UsersQueryParams, "limit" | "page">;

export const FiltersNavbar = (props: Props) => {
  const { usersQueryParams, updateUsersQueryParams } = props;
  const [exclusiveFilter, setExclusiveFilter] =
    useState<ExclusiveFilterKeys>("nameFilter");
  const [fieldError, setFieldError] = useState<ErrorState | null>(null);
  const [filterState, setFilterState] = useState<FilterState>({
    [exclusiveFilter]: usersQueryParams[exclusiveFilter] ?? "",
  });

  const debouncedCallback = useDebouncedCallback(
    (value: string, name: string) => {
      updateUsersQueryParams({
        ...usersInitialState.usersQueryParams,
        [name]: value,
        page: 0,
      });
    },
    800
  );

  const handleFilterInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    const name = event.target.name;
    setFilterState((old) => ({ ...old, [name]: value }));
    if (name === "trackingIdFilter" && value.trim() && !isUuid(value)) {
      setFieldError({
        trackingIdFilter: { message: "Tracking ID must be a UUID" },
      });
      return;
    }

    setFieldError(null);
    debouncedCallback(value, name);
  };

  const clearCurrentFilter = useCallback(() => {
    setFilterState((old) => ({
      ...old,
      [exclusiveFilter]: usersInitialState.usersQueryParams[exclusiveFilter],
    }));
    updateUsersQueryParams({
      ...usersInitialState.usersQueryParams,
      [exclusiveFilter]: usersInitialState.usersQueryParams[exclusiveFilter],
      page: 0,
    });
    blurActiveElement();
  }, [exclusiveFilter, updateUsersQueryParams]);

  const selectedFilter = exclusiveFilters.find(
    (filter) => filter.key === exclusiveFilter
  );

  return (
    <FiltersContainer>
      <FormControl>
        <InputLabel id="filter-by-label">Filter by</InputLabel>
        <Select
          labelId="filter-by-label"
          name="filter-by"
          id="filter-by"
          label="Filter by"
          onChange={(e) => {
            const value = e.target.value as ExclusiveFilterKeys;
            setExclusiveFilter(value);
            if (filterState[value]) {
              updateUsersQueryParams({
                ...usersInitialState.usersQueryParams,
                [value]: filterState[value],
                page: 0,
              });
            }
          }}
          value={exclusiveFilter ?? ""}
        >
          {exclusiveFilters.map((filter) => (
            <MenuItem key={filter.key} value={filter.key}>
              {filter.name}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText> </FormHelperText>
      </FormControl>
      <TextField
        name={exclusiveFilter}
        placeholder={selectedFilter?.name}
        onChange={handleFilterInputChange}
        value={filterState[exclusiveFilter] ?? ""}
        fieldError={fieldError?.[exclusiveFilter]}
        InputProps={{
          endAdornment: (
            <InputAdornment position="start">
              <IconButton size="small" onClick={clearCurrentFilter}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </FiltersContainer>
  );
};
